"use client"

import * as React from "react"
import { SidebarBrandMark } from "@/components/brand/sidebar-brand-mark"
import type { LucideIcon } from "lucide-react"
import {
  BookOpen,
  ClipboardList,
  Layers,
  LayoutDashboard,
  Mail,
  Phone,
  Megaphone,
  MessageSquareText,
  Radar,
  BarChart3,
  Fish,
  ScrollText,
  ShieldAlert,
  MailWarning,
  Settings,
  Sparkles,
  Trophy,
  Users,
  Workflow,
  Zap,
} from "lucide-react"

import { ROUTES } from "@/lib/routes"
import { SETTINGS_NAV, navText } from "@/lib/settings-nav"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { AppVersion } from "@/components/app-version"
import { TeamSwitcher } from "@/components/team-switcher"
import { StagingBadge } from "@/components/staging-watermark"
import { AppThemeScope } from "@/components/app-theme/app-theme-scope"
import { useAppSidebarVariant } from "@/lib/theme/app-themes/use-app-theme"
import { useFeatureVisibility } from "@/hooks/use-feature-visibility"
import { useEnabledModules } from "@/hooks/use-enabled-modules"
import { usePermissions } from "@/hooks/use-permissions"
import { filterNavByPermission } from "@/lib/auth/nav-permissions"
import { usePolicyApprovalsCount } from "@/lib/policies/hooks"
import { useAuthStore } from "@/stores/auth-store"
import { isCompanyTierRole } from "@/lib/auth/company-tier"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"
import { resolveBackendFileUrl } from "@/lib/api/config"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"


// Every item is written with its ENGLISH `title` and the catalogue key of the
// same words (`i18nKey`). buildNavItems returns `title` in the interface
// language and keeps the English one as `id`: NavMain remembers the open
// sections by `id`, so they survive a language switch. The English literals
// stay in the source on purpose: they are the fallback when `t` has no answer,
// and two guards read them (PhishTrainer breadcrumbs, Content Studio wiring).
type NavLabel = {
  title: string
  /** Catalogue key of `title`; absent only for a product name. */
  i18nKey?: string
  /** Language-independent identity (the English title), set by buildNavItems. */
  id?: string
}

type NavSubSubItem = NavLabel & {
  url: string
}

type NavSubItem = NavLabel & {
  url: string
  items?: NavSubSubItem[]
  /** Module entitlement key for one page of a group (the group itself may be
   *  gated on another): hidden unless the tenant has it, fail-open like NavItem. */
  moduleKey?: string
  /** A count shown after the title (nav-main renders it): Policies' "waiting for your approval". */
  badge?: React.ReactNode
  /** Pages this item owns that its url is not a prefix of (see NavMain's `activeFor`). */
  activeFor?: RegExp
}

type NavItem = NavLabel & {
  url: string
  icon?: LucideIcon
  isActive?: boolean
  items?: NavSubItem[]
  /** Module entitlement key. When set, the item is hidden unless the tenant
   *  has this module enabled (or the enabled list is unknown — fail-open). */
  moduleKey?: string
}

// Dev-tool gate: the State Machines visualizer is a behind-the-curtain
// diagnostic for the XState wizards. Hidden from EVERY sidebar (2026-06-09) —
// the empty allowlist means the spread-conditional below always yields []
// so the nav entry never renders. The /state-machines routes still exist and
// are reachable by direct URL for engineers; only the sidebar entry is gone.
// To re-surface for a specific engineer, add their lowercased email here.
const STATE_MACHINES_VISIBLE_EMAILS = new Set<string>([])

// Exported (pure: features/t/userEmail in, nav tree out) so the nav contract
// can be asserted from a test without mounting the whole app shell.
export function buildNavItems(
  features: ReturnType<typeof useFeatureVisibility>,
  t: (key: string) => string,
  userEmail: string | null,
  /** `companyTier`: a Company Owner or Company Admin (lib/auth/company-tier.ts). */
  viewer: { companyTier?: boolean } = {},
): NavItem[] {
  // ── Recipients ─────────────────────────────────────────────────────────────
  const recipientItems: NavSubItem[] = [
    { title: 'Getting started', url: "/recipients/getting-started", i18nKey: 'shell.nav.gettingStarted' },
    // AI Chat removed from the Recipients sub-nav 2026-05-26 — it's
    // already reachable from the top-level "Ask HF AI" shortcut
    // (rendered above the Recipients section), so duplicating it here
    // just clutters the sidebar.
    { title: 'All Recipients', url: "/recipients", i18nKey: 'nav.allRecipients' },
    { title: 'Org Chart', url: "/recipients/org-chart", i18nKey: 'nav.orgChart' },
    { title: 'Groups', url: "/recipients/groups", i18nKey: 'nav.groups' },
    { title: 'Pending Actions', url: "/recipients/pending-actions", i18nKey: 'nav.pendingActions' },
    {
      title: 'Data',
      i18nKey: 'nav.data',
      url: "#",
      items: [
        { title: 'Integrations', url: "/recipients/integrations", i18nKey: 'nav.integrations' },
        { title: 'AD Syncer', url: "/recipients/ad-syncer", i18nKey: 'shell.nav.adSyncer' },
        { title: 'Import CSV/Excel', url: "/recipients/import", i18nKey: 'nav.importCsv' },
        { title: 'Import History', url: "/recipients/imports", i18nKey: 'nav.importHistory' },
        { title: 'Data Health', url: "/recipients/data-quality", i18nKey: 'nav.dataHealth' },
      ],
    },
    {
      title: 'Settings',
      i18nKey: 'nav.settings',
      url: "#",
      items: [
        { title: 'General', url: "/recipients/settings/general", i18nKey: 'nav.general' },
        { title: 'Custom Fields', url: "/recipients/fields", i18nKey: 'nav.customFields' },
        { title: 'Tags', url: "/recipients/settings/tags", i18nKey: 'nav.tags' },
        { title: 'Suppression List', url: "/recipients/suppression-list", i18nKey: 'nav.suppressionList' },
        { title: 'Recycle Bin', url: "/recipients/recycle-bin", i18nKey: 'nav.recycleBin' },
        { title: 'Tooltip Config', url: "/recipients/settings/tooltip", i18nKey: 'nav.tooltipConfig' },
        { title: 'AI Chat Security', url: "/recipients/settings/ai-chat", i18nKey: 'nav.aiChatSecurity' },
        // "Activity Log" renamed → "Audit Log" + moved here under Settings; it's
        // the standard AuditLogsView (module='Recipients'), same design as other settings.
        { title: 'Audit Log', url: "/recipients/audit-log", i18nKey: 'shell.nav.auditLog' },
      ],
    },
  ]

  // ── Global Settings ────────────────────────────────────────────────────────
  // Single source of truth: lib/settings-nav.ts (shared with the /settings
  // hub page — Settings IA redesign 2026-06-10). Groups render as collapsible
  // sub-sections (Account / Workspace / Delivery & Domains / Branding /
  // Company); NavMain already supports the 3-level nesting. Workspace →
  // General stays reachable on every tenant (identity-domain management
  // lives there — do not re-gate on features.showWorkspaceSelector).
  const globalSettingsItems: NavSubItem[] = SETTINGS_NAV.map((group) => ({
    title: group.title,
    i18nKey: group.i18nKey,
    url: '#',
    items: group.items
      .filter((leaf) => !leaf.hubOnly)
      .map((leaf) => ({
        title: leaf.title,
        i18nKey: leaf.i18nKey,
        url: leaf.url,
      })),
  }))

  const tree: NavItem[] = [
    // ── Home (top-level) ──────────────────────────────────────────────────────
    {
      title: 'Home',
      i18nKey: 'shell.nav.home',
      url: '/dashboard',
      icon: LayoutDashboard,
    },
    // ── Ask HF AI (top-level shortcut) ────────────────────────────────────────
    {
      title: 'Ask HF AI',
      i18nKey: 'shell.nav.askHfAi',
      url: '/recipients/ai-chat',
      icon: Sparkles,
    },
    // ── Recipients ────────────────────────────────────────────────────────────
    {
      title: 'Recipients',
      i18nKey: 'nav.recipients',
      url: "#",
      icon: Users,
      isActive: true,
      items: recipientItems,
    },
    // ── vCRO (Virtual Cyber Risk Officer) ─────────────────────────────────────
    // The human-risk layer (docs/product/modules/vcro). Wave 1 is People; wave 2
    // adds the Riskometer (first), Where the risk sits and, for a company owner
    // or admin of a company with several workspaces, Workspaces (a workspace
    // role never sees a sibling workspace or the company total); wave 3 adds
    // Watchlists, Actions and Settings (last, read-only for most); wave 4 adds
    // Signals and the Score model after Actions. Gated on the
    // 'vcro' entitlement. components/vcro/__tests__/vcro-wiring.guard.spec.ts
    // keeps this block, ROUTE_MODULE and lib/routes.ts in lockstep.
    {
      title: 'vCRO',
      i18nKey: 'shell.nav.vcro',
      url: '#',
      icon: Radar,
      moduleKey: 'vcro',
      items: [
        { title: 'Getting started', url: ROUTES.vcro.gettingStarted, i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Riskometer', url: ROUTES.vcro.riskometer, i18nKey: 'shell.nav.riskometer' },
        { title: 'People', url: ROUTES.vcro.people, i18nKey: 'shell.nav.people' },
        { title: 'Where the risk sits', url: ROUTES.vcro.breakdown, i18nKey: 'shell.nav.whereTheRiskSits' },
        { title: 'Watchlists', url: ROUTES.vcro.watchlists, i18nKey: 'shell.nav.watchlists' },
        { title: 'Actions', url: ROUTES.vcro.actions, i18nKey: 'shell.nav.actions' },
        // wave 4 (W4-KNOW): what feeds the score, and how it is built.
        { title: 'Signals', url: ROUTES.vcro.signals, i18nKey: 'shell.nav.signals' },
        { title: 'Score model', url: ROUTES.vcro.model, i18nKey: 'shell.nav.scoreModel' },
        ...(features.showAllWorkspacesView && viewer.companyTier
          ? [{ title: 'Workspaces', url: ROUTES.vcro.workspaces, i18nKey: 'shell.nav.workspaces' }]
          : []),
        // Everyone sees Settings: those who cannot edit read it (the API's canEdit decides).
        { title: 'Settings', url: ROUTES.vcro.settings, i18nKey: 'nav.settings' },
      ],
    },
    // ── Workflows ─────────────────────────────────────────────────────────────
    // Gated on the 'workflows' entitlement (toggleable, off by default for every
    // tier) — the whole group hides unless the tenant has it enabled. Workflows
    // orchestrate recipient journeys across phishing/LMS; Lists = the dedicated
    // workflow-audience surface at /workflows/lists (backed by recipient_groups,
    // the same data the Recipients › Groups manager edits).
    {
      title: 'Workflows',
      i18nKey: 'shell.nav.workflows',
      url: '#',
      icon: Workflow,
      moduleKey: 'workflows',
      items: [
        { title: 'Getting started', url: '/workflows/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'All Workflows',   url: '/workflows', i18nKey: 'shell.nav.allWorkflows' },
        // The ready-made workflow library: a template is copied in as a Draft.
        { title: 'Templates',       url: '/workflows/templates', i18nKey: 'shell.nav.templates' },
        { title: 'Review Queue',    url: '/review-queue', i18nKey: 'shell.nav.reviewQueue' },
        { title: 'Lists',           url: '/workflows/lists', i18nKey: 'shell.nav.lists' },
        { title: 'Email Templates', url: '/workflows/email-templates', i18nKey: 'shell.nav.emailTemplates' },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Email Settings', url: '/workflows/email-settings', i18nKey: 'shell.nav.emailSettings' },
            { title: 'Email Logs',     url: '/workflows/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs',     url: '/workflows/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── MSSP Reports ──────────────────────────────────────────────────────────
    {
      title: 'Executive Reports',
      i18nKey: 'shell.nav.executiveReports',
      url: '#',
      icon: BarChart3,
      moduleKey: 'reports',
      items: [
        { title: 'Dashboard',          url: '/reports', i18nKey: 'shell.nav.dashboard' },
        { title: 'Campaign Analytics', url: '/reports/analytics', i18nKey: 'shell.nav.campaignAnalytics' },
        { title: 'Entities',           url: '/reports/entities', i18nKey: 'shell.nav.entities' },
        { title: 'Users',              url: '/reports/users', i18nKey: 'shell.nav.users' },
        { title: 'Anomalies',          url: '/reports/anomalies', i18nKey: 'shell.nav.anomalies' },
        { title: 'L3 Compromised',     url: '/reports/l3', i18nKey: 'shell.nav.l3Compromised' },
        { title: 'False Positives',    url: '/reports/false-positives', i18nKey: 'shell.nav.falsePositives' },
        { title: 'Risk Bot',           url: '/reports/risk-bot', i18nKey: 'shell.nav.riskBot' },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [{ title: 'Risk Thresholds', url: '/reports/settings', i18nKey: 'shell.nav.riskThresholds' }],
        },
      ],
    },
    // ── Smishing (Mobile Phishing) ──────────────────────────────────────────────
    {
      title: 'Smishing',
      i18nKey: 'shell.nav.smishing',
      url: '#',
      icon: MessageSquareText,
      moduleKey: 'smishing',
      items: [
        { title: 'Getting started', url: '/smishing/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard', url: '/smishing', i18nKey: 'shell.nav.dashboard' },
        { title: 'Campaigns', url: '/smishing/campaigns', i18nKey: 'shell.nav.campaigns' },
        { title: 'Templates', url: '/smishing/templates', i18nKey: 'shell.nav.templates' },
        {
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Reports Dashboard',      url: ROUTES.smishing.reports, i18nKey: 'shell.nav.reportsDashboard' },
            // Worded as each report page's own title (nav.*), not Phishing's report names.
            { title: 'Executive summary',          url: ROUTES.smishing.reportsExecutiveSummary, i18nKey: 'nav.executiveSummary' },
            { title: 'Campaign performance',       url: ROUTES.smishing.reportsCampaigns, i18nKey: 'nav.campaignPerformance' },
            { title: 'Funnel and time to tap',     url: ROUTES.smishing.reportsFunnel, i18nKey: 'nav.funnelTimeToTap' },
            { title: 'Channels',                   url: ROUTES.smishing.reportsChannels, i18nKey: 'nav.channels' },
            { title: 'Template effectiveness',     url: ROUTES.smishing.reportsTemplates, i18nKey: 'nav.templateEffectiveness' },
            { title: 'Departments and job titles', url: ROUTES.smishing.reportsDepartments, i18nKey: 'nav.departmentsAndJobTitles' },
            { title: 'People',                     url: ROUTES.smishing.reportsPeople, i18nKey: 'shell.nav.people' },
            { title: 'Trends',                     url: ROUTES.smishing.reportsTrends, i18nKey: 'nav.trends' },
            { title: 'Delivery health',            url: ROUTES.smishing.reportsDelivery, i18nKey: 'nav.deliveryHealth' },
          ],
        },
        { title: 'Settings', url: '/smishing/settings', i18nKey: 'shell.nav.settings' },
      ],
    },
    // ── Phishing ──────────────────────────────────────────────────────────────
    {
      title: 'Vishing',
      i18nKey: 'vishing.nav.title',
      url: ROUTES.vishing.overview,
      icon: Phone,
      moduleKey: 'vishing',
      items: [
        // Getting started leads every module menu (getting-started-first.guard.spec.tsx).
        { title: 'Getting started', i18nKey: 'vishing.nav.gettingStarted', url: ROUTES.vishing.gettingStarted },
        { title: 'Overview', i18nKey: 'vishing.nav.overview', url: ROUTES.vishing.overview },
        { title: 'Voices', i18nKey: 'vishing.nav.voices', url: ROUTES.vishing.voices },
        { title: 'Scenarios', i18nKey: 'vishing.nav.scenarios', url: ROUTES.vishing.scenarios },
        { title: 'Voice lab', i18nKey: 'vishing.nav.lab', url: ROUTES.vishing.lab },
        { title: 'Phone practice', i18nKey: 'vishing.nav.phonePractice', url: ROUTES.vishing.phonePractice },
        { title: 'Campaigns', i18nKey: 'vishing.nav.campaigns', url: ROUTES.vishing.campaigns },
        { title: 'Campaign templates', i18nKey: 'vishing.nav.templates', url: ROUTES.vishing.templates },
        {
          title: 'Reports',
          i18nKey: 'vishing.nav.reports',
          url: '#',
          items: [
            { title: 'Reports Dashboard',      url: ROUTES.vishing.reports, i18nKey: 'shell.nav.reportsDashboard' },
            // Sentence case, worded as each report page's own title.
            { title: 'Executive summary',      url: ROUTES.vishing.reportPages.executiveSummary, i18nKey: 'nav.executiveSummary' },
            { title: 'Trends',                 url: ROUTES.vishing.reportPages.trends, i18nKey: 'nav.trends' },
            { title: 'Scenario effectiveness', url: ROUTES.vishing.reportPages.effectiveness, i18nKey: 'vishing.nav.effectiveness' },
            { title: 'Objective outcomes',     url: ROUTES.vishing.reportPages.objectives, i18nKey: 'vishing.nav.objectives' },
            { title: 'Call operations',        url: ROUTES.vishing.reportPages.callOperations, i18nKey: 'vishing.nav.callOperations' },
            { title: 'Repeat offenders',       url: ROUTES.vishing.reportPages.repeatOffenders, i18nKey: 'nav.repeatOffenders' },
            { title: 'Weakest links',          url: ROUTES.vishing.reportPages.weakestLinks, i18nKey: 'nav.weakestLinks' },
            { title: 'Strongest links',        url: ROUTES.vishing.reportPages.strongestLinks, i18nKey: 'nav.strongestLinks' },
          ],
        },
        { title: 'Do-not-call list', i18nKey: 'vishing.nav.optOuts', url: ROUTES.vishing.optOuts },
        { title: 'Settings', i18nKey: 'vishing.nav.settings', url: ROUTES.vishing.settings },
        { title: 'Audit log', i18nKey: 'vishing.nav.audit', url: ROUTES.vishing.audit },
      ],
    },
    {
      title: 'Phishing',
      i18nKey: 'shell.nav.phishing',
      url: '#',
      icon: Mail,
      moduleKey: 'phishing',
      items: [
        { title: 'Getting started', url: '/phishing/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard', url: '/phishing', i18nKey: 'shell.nav.dashboard' },
        {
          title: 'Campaigns',
          i18nKey: 'shell.nav.campaigns',
          url: '#',
          items: [
            { title: 'All Campaigns',   url: '/phishing/campaigns', i18nKey: 'shell.nav.allCampaigns' },
            // Pro Create is the default create entry (2026-06-09). Classic
            // wizard stays reachable via the "Switch to classic wizard" link in
            // the Pro toolbar, and `/phishing/campaigns/new` still redirects
            // here for old bookmarks.
            { title: 'Create Campaign', url: '/phishing/campaigns/new', i18nKey: 'shell.nav.createCampaign' },
            // Inbox — per-module URL (2026-05-20 unified-inbox redesign).
            // Each module now owns its own URL backed by <InboxShell>.
            { title: 'Inbox',           url: '/phishing/campaigns/inbox', i18nKey: 'shell.nav.inbox' },
            { title: 'Archive',         url: '/phishing/campaigns/archive', i18nKey: 'shell.nav.archive' },
            { title: 'Inbound Replies', url: '/phishing/inbound-replies', i18nKey: 'shell.nav.inboundReplies' },
          ],
        },
        { title: 'Scenarios', url: '/phishing/scenarios', i18nKey: 'shell.nav.scenarios' },
        { title: 'AI Studio', url: '/phishing/ai-studio', i18nKey: 'shell.nav.aiStudio' },
        {
          title: 'Library',
          i18nKey: 'shell.nav.library',
          url: '#',
          items: [
            { title: 'Email Templates', url: '/phishing/library/emails', i18nKey: 'shell.nav.emailTemplates' },
            { title: 'Phishing Pages',  url: '/phishing/library/phishing-pages', i18nKey: 'shell.nav.phishingPages' },
            { title: 'Landing Pages',   url: '/phishing/library/landing-pages', i18nKey: 'shell.nav.landingPages' },
            { title: 'Brand Themes',    url: '/phishing/library/brand-themes', i18nKey: 'shell.nav.brandThemes' },
          ],
        },
        {
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Reports Dashboard',       url: '/phishing/reports', i18nKey: 'shell.nav.reportsDashboard' },
            { title: 'Activity',                url: '/phishing/reports/activity', i18nKey: 'shell.nav.activity' },
            { title: 'Funnel + Time-to-X',      url: '/phishing/reports/funnel', i18nKey: 'shell.nav.funnel' },
            { title: 'Device Breakdown',        url: '/phishing/reports/devices', i18nKey: 'shell.nav.deviceBreakdown' },
            { title: 'Geography',               url: '/phishing/reports/geography', i18nKey: 'shell.nav.geography' },
            { title: 'Bot Activity',            url: '/phishing/reports/bot-activity', i18nKey: 'shell.nav.botActivity' },
            { title: 'Template Effectiveness',  url: '/phishing/reports/effectiveness', i18nKey: 'shell.nav.templateEffectiveness' },
            { title: 'Executive Summary',       url: '/phishing/reports/executive-summary', i18nKey: 'shell.nav.executiveSummary' },
            { title: 'Campaigns Performance',   url: '/phishing/reports/campaigns-performance', i18nKey: 'shell.nav.campaignsPerformance' },
            { title: 'Vulnerability Trend',     url: '/phishing/reports/vulnerability-trend', i18nKey: 'shell.nav.vulnerabilityTrend' },
            { title: 'Awareness Trend',         url: '/phishing/reports/awareness-trend', i18nKey: 'shell.nav.awarenessTrend' },
            { title: 'Reporting Trend',         url: '/phishing/reports/reporting-trend', i18nKey: 'shell.nav.reportingTrend' },
            { title: 'Weakest Links',           url: '/phishing/reports/weakest-links', i18nKey: 'shell.nav.weakestLinks' },
            { title: 'Strongest Links',         url: '/phishing/reports/strongest-links', i18nKey: 'shell.nav.strongestLinks' },
            { title: 'Top Reporters',           url: '/phishing/reports/top-reporters', i18nKey: 'shell.nav.topReporters' },
            { title: 'Repeat Offenders',        url: '/phishing/reports/repeat-offenders', i18nKey: 'shell.nav.repeatOffenders' },
            { title: 'Departments',             url: '/phishing/reports/departments', i18nKey: 'shell.nav.departments' },
            { title: 'Designations',            url: '/phishing/reports/designations', i18nKey: 'shell.nav.designations' },
          ],
        },
        { title: 'Categories', url: '/phishing/categories', i18nKey: 'shell.nav.categories' },
        { title: 'Tags',       url: '/phishing/settings/tags', i18nKey: 'nav.tags' },
        // ONE domains entry (owner, 2026-10-13). There were three for two things:
        // "Notification Domains" and "Simulation Domains" (links out to Settings
        // pages) and "Mail", the page that sets a simulation domain up. Simulation
        // domains now live only on that page; notification domains are not a
        // phishing matter and stay in Settings → Mail delivery.
        { title: 'Simulation Domains', url: ROUTES.phishing.simulationDomains, i18nKey: 'shell.nav.simulationDomains' },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Email Logs', url: '/phishing/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/phishing/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
        { title: 'Bot Management', url: '/phishing/bot-management', i18nKey: 'shell.nav.botManagement' },
      ],
    },
    // ── Announcements ─────────────────────────────────────────────────────────
    {
      title: 'Announcements',
      i18nKey: 'shell.nav.announcements',
      url: '#',
      // Distinct icon (was `Mail`, identical to Phishing → indistinguishable in the rail).
      icon: Megaphone,
      moduleKey: 'announcements',
      items: [
        { title: 'Getting started', url: '/announcements/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard',        url: '/announcements', i18nKey: 'shell.nav.dashboard' },
        { title: 'Campaigns',        url: '/announcements/campaigns', i18nKey: 'shell.nav.campaigns' },
        // Archived campaigns — was reachable only from a Getting-Started card (phishing parity).
        { title: 'Archive',          url: '/announcements/campaigns/archive', i18nKey: 'shell.nav.archive' },
        // Per-module inbox URL (2026-05-20 unified-inbox redesign).
        { title: 'Inbox (QA / Demo)', url: '/announcements/inbox', i18nKey: 'shell.nav.inboxQaDemo' },
        { title: 'AI Studio',        url: '/announcements/ai-studio', i18nKey: 'shell.nav.aiStudio' },
        {
          title: 'Library',
          i18nKey: 'shell.nav.library',
          url: '#',
          items: [
            { title: 'Email Templates', url: '/announcements/library', i18nKey: 'shell.nav.emailTemplates' },
            { title: 'Landing Pages',   url: '/announcements/library/landing-pages', i18nKey: 'shell.nav.landingPages' },
          ],
        },
        { title: 'Categories',  url: '/announcements/categories', i18nKey: 'shell.nav.categories' },
        {
          // Every report its own item, as Phishing, LMS and JIT Training list
          // theirs (owner, 2 October 2026). Each title is the report page's own
          // heading, so the item, the breadcrumb and the page say the same
          // words. /announcements/reports redirects to the Executive Summary;
          // reporting-trend redirects to Compliance Trend.
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Executive Summary',       url: '/announcements/reports/executive-summary', i18nKey: 'shell.nav.executiveSummary' },
            { title: 'Campaigns Performance',   url: '/announcements/reports/campaigns-performance', i18nKey: 'shell.nav.campaignsPerformance' },
            { title: 'Vulnerability Trend',     url: '/announcements/reports/vulnerability-trend', i18nKey: 'shell.nav.vulnerabilityTrend' },
            { title: 'Awareness Trend',         url: '/announcements/reports/awareness-trend', i18nKey: 'shell.nav.awarenessTrend' },
            { title: 'Compliance Trend',        url: '/announcements/reports/compliance-trend', i18nKey: 'shell.nav.complianceTrend' },
            { title: 'Engagement Trend',        url: '/announcements/reports/engagement-trend', i18nKey: 'shell.nav.engagementTrend' },
            { title: 'Acknowledgment Trend',    url: '/announcements/reports/acknowledgment-trend', i18nKey: 'shell.nav.acknowledgmentTrend' },
            { title: 'Non-Compliant Employees', url: '/announcements/reports/weakest-links', i18nKey: 'shell.nav.nonCompliantEmployees' },
            { title: 'Most Engaged Employees',  url: '/announcements/reports/strongest-links', i18nKey: 'shell.nav.mostEngagedEmployees' },
            { title: 'Repeat Non-Compliance',   url: '/announcements/reports/repeat-offenders', i18nKey: 'shell.nav.repeatNonCompliance' },
            { title: 'Departments',             url: '/announcements/reports/departments', i18nKey: 'shell.nav.departments' },
            { title: 'Designations',            url: '/announcements/reports/designations', i18nKey: 'shell.nav.designations' },
          ],
        },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '/announcements/settings',
          items: [
            { title: 'Tags',       url: '/announcements/settings/tags', i18nKey: 'nav.tags' },
            { title: 'Senders',    url: '/announcements/settings/senders', i18nKey: 'shell.nav.senders' },
            // Per-workspace delivery (sender identity / sending + tracking domain) —
            // was reachable only via the no-sender banner, orphaned once a sender exists.
            { title: 'Delivery',   url: '/announcements/settings/delivery', i18nKey: 'shell.nav.delivery' },
            { title: 'Email Logs', url: '/announcements/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/announcements/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── Surveys (2026-10-12; DHA W1-08 built the first version) ───────────────
    // Rounds sent to an audience, each from a template of the library. The
    // module is opt-in: hidden until a super admin switches it on for the company.
    {
      title: 'Surveys',
      i18nKey: 'shell.nav.surveys',
      url: '#',
      icon: ClipboardList,
      moduleKey: 'surveys',
      items: [
        { title: 'Getting started', url: '/surveys/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        // The module root is the Dashboard since 17 Oct 2026; the rounds table is All Surveys.
        { title: 'Dashboard', url: '/surveys', i18nKey: 'shell.nav.dashboard' },
        {
          title: 'All Surveys',
          url: '/surveys/all',
          i18nKey: 'shell.nav.allSurveys',
          // A round's own pages (/surveys/<id>, /surveys/<id>/results) belong here, not to the Dashboard.
          activeFor: /^\/surveys\/(?!getting-started(?:\/|$)|all(?:\/|$)|new(?:\/|$)|templates(?:\/|$)|reports(?:\/|$)|email-log(?:\/|$)|audit-log(?:\/|$))[^/]+/,
        },
        { title: 'New Survey', url: '/surveys/new', i18nKey: 'shell.nav.newSurvey' },
        { title: 'Templates', url: '/surveys/templates', i18nKey: 'shell.nav.templates' },
        {
          // Every report its own item (owner, 2 Oct 2026; built 17 Oct 2026). Each label is
          // a shell.nav key worded as the report page's own heading, so the item, the
          // breadcrumb (PageShell looks crumbs up in shell.nav) and the page say the same
          // words in every language. /surveys/reports redirects to the Executive Summary.
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Executive Summary',           url: '/surveys/reports/executive-summary', i18nKey: 'shell.nav.executiveSummary' },
            { title: 'Response Rates',              url: '/surveys/reports/response-rates', i18nKey: 'shell.nav.responseRates' },
            { title: 'Risk Culture Signals',        url: '/surveys/reports/risk-culture-signals', i18nKey: 'shell.nav.riskCultureSignals' },
            { title: 'Question Trends',             url: '/surveys/reports/question-trends', i18nKey: 'shell.nav.questionTrends' },
            { title: 'Verbatims',                   url: '/surveys/reports/verbatims', i18nKey: 'shell.nav.verbatims' },
            { title: 'Participation by Group',      url: '/surveys/reports/participation', i18nKey: 'shell.nav.participationByGroup' },
            { title: 'Exports',                     url: '/surveys/reports/exports', i18nKey: 'shell.nav.exports' },
          ],
        },
        // The module's own e-mail and audit logs (2026-10-15), nested like Workflows' and Policies'.
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Email Logs', url: '/surveys/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/surveys/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── Policies (2026-10-12; rebuilt 2026-10-15; v3 2026-10-16, DECISIONS D15) ─
    // Versioned policies people read and acknowledge in the portal: Getting
    // started (the module guide, first as in every module), the Dashboard
    // (where the organisation stands), All Policies (the library and the approval
    // inbox; its badge is what waits for MY decision, fed in AppSidebar),
    // Templates (the policy e-mails, edited in the Email Centre), a Reports
    // group (every report its own item, 17 Oct 2026), and a Settings group: the
    // ONE Settings page and the module's Email Logs and Audit Logs (owner, 17 Oct
    // 2026: the logs are no longer top-level, reversing D15). Opt-in like
    // Surveys. A policy's own pages are reached from All Policies.
    {
      title: 'Policies',
      i18nKey: 'shell.nav.policies',
      url: '#',
      icon: ScrollText,
      moduleKey: 'policies',
      items: [
        { title: 'Getting started', url: '/policies/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard', url: '/policies', i18nKey: 'shell.nav.dashboard' },
        {
          title: 'All Policies',
          url: '/policies/library',
          i18nKey: 'shell.nav.allPolicies',
          // A policy's own pages (/policies/<id>/…, /policies/new) belong here, not to the Dashboard at /policies.
          activeFor: /^\/policies\/(?!getting-started(?:\/|$)|library(?:\/|$)|settings(?:\/|$)|reports(?:\/|$)|templates(?:\/|$)|email-logs(?:\/|$)|audit-logs(?:\/|$))[^/]+/,
        },
        { title: 'Email templates', url: '/policies/templates', i18nKey: 'shell.nav.policyEmailTemplates' },
        {
          // Every report its own item, as Phishing, LMS and Announcements list theirs
          // (owner, 2 Oct 2026; Policies Reports built 17 Oct 2026). /policies/reports
          // redirects to Audit Packs, its address before the section existed.
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Executive Summary',        url: '/policies/reports/executive-summary', i18nKey: 'shell.nav.executiveSummary' },
            { title: 'Compliance by Policy',     url: '/policies/reports/compliance-by-policy', i18nKey: 'shell.nav.complianceByPolicy' },
            { title: 'Compliance by Group', url: '/policies/reports/groups', i18nKey: 'shell.nav.complianceByGroup' },
            { title: 'Acknowledgment Trend',     url: '/policies/reports/acknowledgment-trend', i18nKey: 'shell.nav.acknowledgmentTrend' },
            { title: 'Overdue and Needs Help',   url: '/policies/reports/overdue', i18nKey: 'shell.nav.overdueAndNeedsHelp' },
            { title: 'Exemptions',               url: '/policies/reports/exemptions', i18nKey: 'shell.nav.exemptions' },
            { title: 'Language Coverage',        url: '/policies/reports/languages', i18nKey: 'shell.nav.languageCoverage' },
            { title: 'Versions and Reviews',     url: '/policies/reports/versions', i18nKey: 'shell.nav.versionsAndReviews' },
            { title: 'Reading and Questions',         url: '/policies/reports/reading', i18nKey: 'shell.nav.readingAndQuestions' },
            { title: 'Audit Packs',              url: '/policies/reports/audit-packs', i18nKey: 'shell.nav.auditPacks' },
          ],
        },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Settings', url: '/policies/settings', i18nKey: 'shell.nav.settings' },
            { title: 'Email Logs', url: '/policies/email-logs', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/policies/audit-logs', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── LMS ───────────────────────────────────────────────────────────────────
    {
      title: 'LMS',
      i18nKey: 'shell.nav.lms',
      url: '#',
      icon: BookOpen,
      moduleKey: 'lms',
      items: [
        { title: 'Getting started', url: '/lms/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard',   url: '/lms', i18nKey: 'shell.nav.dashboard' },
        { title: 'Live',        url: '/lms/live', i18nKey: 'shell.nav.live' },
        { title: 'Courses',     url: '/lms/courses', i18nKey: 'shell.nav.courses' },
        { title: 'Enrollments', url: '/lms/enrollments', i18nKey: 'shell.nav.enrollments' },
        { title: 'Assignments', url: '/lms/assignments', i18nKey: 'shell.nav.assignments' },
        {
          title: 'Automations',
          i18nKey: 'shell.nav.automations',
          url: '#',
          // The classic LMS automation builder is its own module (2026-10-08),
          // retired for every company since 2026-10-10: shown only where a
          // super admin's override gives it.
          moduleKey: 'lms-automations',
          items: [
            { title: 'All Rules',  url: '/lms/automations', i18nKey: 'shell.nav.allRules' },
            { title: 'Templates', url: '/lms/automations/templates', i18nKey: 'shell.nav.templates' },
          ],
        },
        {
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Executive Summary', url: '/lms/reports/v2/executive-summary', i18nKey: 'shell.nav.executiveSummary' },
            { title: 'Learners',          url: '/lms/reports/v2/learners', i18nKey: 'shell.nav.learners' },
            { title: 'Departments',       url: '/lms/reports/v2/departments', i18nKey: 'shell.nav.departments' },
            { title: 'Managers',          url: '/lms/reports/v2/managers', i18nKey: 'shell.nav.managers' },
            { title: 'Courses',           url: '/lms/reports/v2/courses', i18nKey: 'shell.nav.courses' },
            { title: 'Overdue',           url: '/lms/reports/v2/overdue', i18nKey: 'shell.nav.overdue' },
            { title: 'In Progress',       url: '/lms/reports/v2/in-progress', i18nKey: 'shell.nav.inProgress' },
            { title: 'Completed',         url: '/lms/reports/v2/completed', i18nKey: 'shell.nav.completed' },
            { title: 'Failed',            url: '/lms/reports/v2/failed', i18nKey: 'shell.nav.failed' },
            { title: 'Non-Starters',      url: '/lms/reports/v2/non-starters', i18nKey: 'shell.nav.nonStarters' },
            { title: 'Repeat Failures',   url: '/lms/reports/v2/repeat-failures', i18nKey: 'shell.nav.repeatFailures' },
          ],
        },
        { title: 'Sent Emails', url: '/lms/sent-emails', i18nKey: 'shell.nav.sentEmails' },
        // Per-module inbox URL (2026-05-20 unified-inbox redesign).
        { title: 'Inbox (QA / Demo)', url: '/lms/inbox', i18nKey: 'shell.nav.inboxQaDemo' },
        { title: 'Recycle Bin', url: '/lms/recycle-bin', i18nKey: 'nav.recycleBin' },
        {
          // Per-module logs live NESTED under the module's Settings (mirrors the
          // main sidebar's Settings). Every module follows: Settings → Main /
          // Email Logs / Audit Logs.
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Main',       url: '/lms/settings', i18nKey: 'shell.nav.main' },
            { title: 'Email Logs', url: '/lms/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/lms/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
        { title: 'Bot Management', url: '/lms/bot-management', i18nKey: 'shell.nav.botManagement' },
      ],
    },
    // ── Content Studio ────────────────────────────────────────────────────────
    // Card-deck authoring + SCORM Forge. Sits right after LMS (its advisory
    // dependsOn — decks publish into LMS courses). Gated on the
    // 'content-studio' entitlement; the wiring guard
    // (lib/__tests__/content-studio-wiring.guard.spec.ts) asserts this block,
    // the ROUTE_MODULE entry, and the BE registry entry stay in lockstep.
    {
      title: 'Content Studio',
      i18nKey: 'shell.nav.contentStudio',
      url: '#',
      icon: Layers,
      moduleKey: 'content-studio',
      items: [
        { title: 'Getting started', url: '/content-studio/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Create training', url: '/content-studio/create', i18nKey: 'shell.nav.createTraining' },
        { title: 'Decks',           url: '/content-studio', i18nKey: 'shell.nav.decks' },
        { title: 'Videos',          url: '/content-studio/videos', i18nKey: 'shell.nav.videos' },
        { title: 'Theme & options', url: '/content-studio/settings', i18nKey: 'shell.nav.themeAndOptions' },
        { title: 'Free Form',       url: '/content-studio/free-form', i18nKey: 'shell.nav.freeForm' },
      ],
    },
    // ── JIT Training (Just-in-Time Coaching) ──────────────────────────────────
    {
      title: 'JIT Training',
      i18nKey: 'shell.nav.jitTraining',
      url: '#',
      icon: Zap,
      moduleKey: 'jit-training',
      items: [
        { title: 'Getting started', url: '/jit-training/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard',       url: '/jit-training', i18nKey: 'shell.nav.dashboard' },
        { title: 'Detection Rules', url: '/jit-training/detection-rules', i18nKey: 'shell.nav.detectionRules' },
        { title: 'Coaching Tips',   url: '/jit-training/coaching-tips', i18nKey: 'shell.nav.coachingTips' },
        { title: 'Notification Templates', url: '/jit-training/coaching-tips/templates', i18nKey: 'shell.nav.notificationTemplates' },
        { title: 'Coaching Campaigns', url: '/jit-training/campaigns', i18nKey: 'shell.nav.coachingCampaigns' },
        {
          title: 'Setup',
          i18nKey: 'shell.nav.setup',
          url: '#',
          items: [
            { title: 'Security Vendor Integrations', url: '/jit-training/setup/integrations', i18nKey: 'shell.nav.securityVendorIntegrations' },
            { title: 'Email Delivery', url: '/jit-training/setup/email', i18nKey: 'shell.nav.emailDelivery' },
            // Agent device pairing/revoke — was reachable only mid-flow from the setup wizard.
            { title: 'Agent Devices', url: '/jit-training/setup/agent-devices', i18nKey: 'shell.nav.agentDevices' },
          ],
        },
        {
          title: 'Reports',
          i18nKey: 'shell.nav.reports',
          url: '#',
          items: [
            { title: 'Coaching Activity Log', url: '/jit-training/reports/coaching-activity', i18nKey: 'shell.nav.coachingActivityLog' },
            { title: 'Channel Effectiveness', url: '/jit-training/reports/channels', i18nKey: 'shell.nav.channelEffectiveness' },
            { title: 'Template Effectiveness', url: '/jit-training/reports/templates', i18nKey: 'shell.nav.templateEffectiveness' },
            { title: 'Cyber-insurance packet', url: '/jit-training/reports/cyber-insurance', i18nKey: 'shell.nav.cyberInsurancePacket' },
          ],
        },
        { title: 'Manager view', url: '/jit-training/manager', i18nKey: 'shell.nav.managerView' },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Email Logs', url: '/jit-training/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/jit-training/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── Email Security (ESA) ──────────────────────────────────────────────────
    {
      title: 'Email Security (ESA)',
      i18nKey: 'shell.nav.emailSecurity',
      url: '#',
      icon: ShieldAlert,
      moduleKey: 'esa',
      items: [
        { title: 'ESA Sets',  url: '/esa/settings-sets', i18nKey: 'shell.nav.esaSets' },
        { title: 'Subscriptions',  url: '/esa/subscriptions', i18nKey: 'shell.nav.subscriptions' },
        { title: 'Mark-safe Senders', url: '/esa/sender-exceptions', i18nKey: 'shell.nav.markSafeSenders' },
        { title: 'Trusted Senders', url: '/esa/trusted-senders', i18nKey: 'shell.nav.trustedSenders' },
        { title: 'Email Logs',           url: '/esa/logs', i18nKey: 'shell.nav.emailLogs' },
        { title: 'Monitor Inbox',  url: '/esa/monitor', i18nKey: 'shell.nav.monitorInbox' },
        { title: 'Reports',        url: '/esa/reports', i18nKey: 'shell.nav.reports' },
        // Settings sub-group — mirrors the Workflows/Reports "Settings" nested
        // pattern (NavSubItem.items renders as a Collapsible in nav-main.tsx).
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'ESA Policy',   url: '/esa/settings', i18nKey: 'shell.nav.esaPolicy' },
            { title: 'Integration',  url: '/esa/integration', i18nKey: 'shell.nav.integration' },
            { title: 'Audit Logs',   url: '/esa/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── Email Remediator (ER) ─────────────────────────────────────────────────
    // Customer-facing label is always "Email Remediator" (route prefix stays er).
    {
      title: 'Email Remediator',
      i18nKey: 'shell.nav.emailRemediator',
      url: '#',
      icon: MailWarning,
      moduleKey: 'er',
      items: [
        { title: 'Getting started',    url: '/er/getting-started', i18nKey: 'shell.nav.gettingStarted' },
        { title: 'Dashboard',          url: '/er', i18nKey: 'shell.nav.dashboard' },
        { title: 'Reported Mails',     url: '/er/reported-mails', i18nKey: 'shell.nav.reportedMails' },
        { title: 'Search & Remediate', url: '/er/search-remediate', i18nKey: 'shell.nav.searchRemediate' },
        // Threat Logs hidden for now (user request) — nav item + pages disabled.
        // { title: 'Threat Logs',        url: '/er/threat-logs' },
        { title: 'Reports',            url: '/er/reports', i18nKey: 'shell.nav.reports' },
        { title: 'Leaderboard',        url: '/er/leaderboard', i18nKey: 'shell.nav.leaderboard' },
        { title: 'Email Logs',         url: '/er/logs', i18nKey: 'shell.nav.emailLogs' },
        // Settings sub-group — mirrors the ESA "Settings" nested pattern
        // (NavSubItem.items renders as a Collapsible in nav-main.tsx).
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Remediator Policy',      url: '/er/settings', i18nKey: 'shell.nav.remediatorPolicy' },
            { title: 'Integration',            url: '/er/integration', i18nKey: 'shell.nav.integration' },
            { title: 'Dispositions & Actions', url: '/er/settings/catalogs', i18nKey: 'shell.nav.dispositionsActions' },
            { title: 'Add-in Branding',        url: '/er/settings/branding', i18nKey: 'shell.nav.addinBranding' },
            { title: 'Colleague Report Tags', url: '/er/settings/report-tags', i18nKey: 'shell.nav.colleagueReportTags' },
            // The Outlook add-in manifest / deploy page — was built + deployed but had
            // NO inbound link anywhere, so it was completely unreachable in the UI.
            { title: 'Install Extension',      url: '/er/settings/install-extension', i18nKey: 'shell.nav.installExtension' },
            { title: 'Audit Logs',             url: '/er/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── PhishTrainer ──────────────────────────────────────────────────────────
    // Grouped into 3 collapsible sub-sections so the 12-item flat list
    // doesn't dominate the sidebar. NavMain already supports 3-level nesting
    // (used by JIT Training). Section headers (url='#') are collapsible
    // triggers — not navigation targets — so '#' is safe here.
    {
      title: 'PhishTrainer',
      i18nKey: 'shell.nav.phishTrainer',
      url: '/phishtrainer',
      icon: Fish,
      moduleKey: 'phishtrainer',
      items: [
        {
          title: 'Learners',
          i18nKey: 'shell.nav.learners',
          url: '#',
          items: [
            { title: 'Dashboard',   url: '/phishtrainer', i18nKey: 'shell.nav.dashboard' },
            { title: 'Users',       url: '/phishtrainer/users', i18nKey: 'shell.nav.users' },
            { title: 'Inbox',       url: '/phishtrainer/inbox', i18nKey: 'shell.nav.inbox' },
            { title: 'Leaderboard', url: '/phishtrainer/leaderboard', i18nKey: 'shell.nav.leaderboard' },
          ],
        },
        {
          title: 'Training',
          i18nKey: 'shell.nav.training',
          url: '#',
          items: [
            { title: 'Programs',   url: '/phishtrainer/programs', i18nKey: 'shell.nav.programs' },
            { title: 'Reminders',  url: '/phishtrainer/reminders', i18nKey: 'shell.nav.reminders' },
            { title: 'Reports',    url: '/phishtrainer/reports', i18nKey: 'shell.nav.reports' },
            { title: 'Baseline Report', url: '/phishtrainer/reports/baseline', i18nKey: 'shell.nav.baselineReport' },
            { title: 'Engagement', url: '/phishtrainer/engagement', i18nKey: 'shell.nav.engagement' },
          ],
        },
        {
          title: 'Content',
          i18nKey: 'shell.nav.content',
          url: '#',
          items: [
            { title: 'Attack Library',        url: '/phishtrainer/library', i18nKey: 'shell.nav.attackLibrary' },
            // No "Attack Flipper" entry while its page is a "not available yet"
            // placeholder (the .eml ingest is not built): a menu item must not open a
            // coming-soon card. lib/phishtrainer/__tests__/no-entry-to-an-unavailable-feature.guard.spec.ts
            { title: 'AI Generate',           url: '/phishtrainer/generate', i18nKey: 'shell.nav.aiGenerate' },
            { title: 'P.H.I.S.H. Explainer', url: '/phishtrainer/phishflag', i18nKey: 'shell.nav.phishExplainer' },
          ],
        },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Main',       url: '/phishtrainer/settings', i18nKey: 'shell.nav.main' },
            { title: 'Email Logs', url: '/phishtrainer/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/phishtrainer/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── Feedback ──────────────────────────────────────────────────────────────
    {
      title: 'Feedback',
      i18nKey: 'nav.feedback',
      url: "/feedback",
      icon: MessageSquareText,
    },
    // ── Gamification ──────────────────────────────────────────────────────────
    {
      title: 'Gamification',
      i18nKey: 'shell.nav.gamification',
      url: '/gamification',
      icon: Trophy,
      moduleKey: 'gamification',
      items: [
        // NO 'Dashboard' leaf (#247). `/gamification` is a redirect to
        // /gamification/badges — there is no dashboard page — so the entry
        // advertised a destination it did not have and duplicated Badges.
        // The ROUTE stays: components/module-access-guard.tsx and
        // lib/feedback/module-from-pathname.ts both prefix-match '/gamification',
        // and the log pages' historical breadcrumb target is that path.
        { title: 'Badges',       url: '/gamification/badges', i18nKey: 'shell.nav.badges' },
        { title: 'Leaderboard',  url: '/gamification/leaderboard', i18nKey: 'shell.nav.leaderboard' },
        { title: 'Challenges',   url: '/gamification/challenges', i18nKey: 'shell.nav.challenges' },
        { title: 'Activity Log', url: '/gamification/activity-log', i18nKey: 'nav.activityLog' },
        {
          title: 'Settings',
          i18nKey: 'nav.settings',
          url: '#',
          items: [
            { title: 'Set Points', url: '/gamification/set-points', i18nKey: 'shell.nav.setPoints' },
            { title: 'Defaults',   url: '/gamification/defaults', i18nKey: 'shell.nav.defaults' },
            { title: 'Email Logs', url: '/gamification/email-log', i18nKey: 'shell.nav.emailLogs' },
            { title: 'Audit Logs', url: '/gamification/audit-log', i18nKey: 'shell.nav.auditLogs' },
          ],
        },
      ],
    },
    // ── User Portal ───────────────────────────────────────────────────────────
    // 2026-05-21 (T2): Teams Portal collapsed into User Portal as flat
    // sub-items. Mode lives in the URL path (/settings/portal/[mode]/[tab]).
    // No on-page mode switch. Old /teams-portal/* and /settings/team-portal/*
    // URLs return 404 — sidebar drives discovery.
    {
      title: 'User Portal',
      i18nKey: 'shell.nav.userPortal',
      url: '/settings/portal',
      icon: LayoutDashboard,
      moduleKey: 'user-portal',
      items: [
        { title: 'Themes',                   url: '/settings/portal/employee/theme', i18nKey: 'shell.nav.themes' },
        { title: 'Customise Portal',         url: '/settings/portal/employee/customize', i18nKey: 'shell.nav.customisePortal' },
        { title: 'Customise Login',          url: '/settings/portal/employee/login-customize', i18nKey: 'shell.nav.customiseLogin' },
        { title: 'View as Employee',         url: '/settings/portal/employee/view-as', i18nKey: 'shell.nav.viewAsEmployee' },
        { title: 'View as Manager',          url: '/settings/portal/manager/view-as', i18nKey: 'shell.nav.viewAsManager' },
        { title: 'News & Feeds',             url: '/settings/portal/employee/news', i18nKey: 'shell.nav.newsFeeds' },
        // Knowledge & assistants (docs/product/modules/knowledge): its own menu
        // inside User Portal, gated on the `knowledge` module, so its guide can
        // lead it like every module's (getting-started-first.guard.spec.tsx).
        // components/knowledge/__tests__/knowledge-wiring.guard.spec.tsx keeps
        // these, ROUTE_MODULE and lib/routes.ts in lockstep.
        {
          title: 'Knowledge & assistants',
          i18nKey: 'shell.nav.knowledgeAndAssistants',
          url: '#',
          moduleKey: 'knowledge',
          items: [
            { title: 'Getting started',    url: ROUTES.knowledge.gettingStarted, i18nKey: 'shell.nav.gettingStarted' },
            { title: 'Knowledge base',     url: ROUTES.knowledge.base, i18nKey: 'shell.nav.knowledgeBase' },
            { title: 'Knowledge insights', url: ROUTES.knowledge.insights, i18nKey: 'shell.nav.knowledgeInsights' },
            { title: 'Employee assistant', url: ROUTES.knowledge.assistant('employee'), i18nKey: 'shell.nav.employeeAssistant' },
            { title: 'Manager assistant',  url: ROUTES.knowledge.assistant('manager'), i18nKey: 'shell.nav.managerAssistant' },
          ],
        },
      ],
    },
    // ── Settings ──────────────────────────────────────────────────────────────
    {
      title: 'Settings',
      i18nKey: 'nav.settings',
      url: "/settings",
      icon: Settings,
      items: globalSettingsItems,
    },
  ]

  return localizeNav(tree, (english, key) => navText(t, english, key))
}

type LocalizableNav = NavLabel & { items?: LocalizableNav[] }

/**
 * The tree in the interface language. Each `title` becomes the translation of
 * its `i18nKey` (English when `t` has none: outside the provider, or a `t`
 * that echoes the key, never a raw key path), and the English title is kept
 * as `id`.
 */
function localizeNav<T extends LocalizableNav>(
  items: T[],
  label: (english: string, key?: string) => string,
): T[] {
  return items.map((item) => ({
    ...item,
    id: item.id ?? item.title,
    title: label(item.title, item.i18nKey),
    ...(item.items ? { items: localizeNav(item.items, label) } : {}),
  })) as T[]
}

/**
 * Policies' sidebar badge: the count of drafts waiting for the signed-in approver, on "All Policies".
 * The visible number is decorative; the sentence ("3 waiting for your approval") is the accessible name.
 * Pure (the tree in, the tree out), so a spec can assert it without mounting the shell.
 */
export function withPolicyApprovalBadge<T extends { id?: string; items?: Array<{ url: string; badge?: React.ReactNode }> }>(
  items: T[],
  count: number,
  label: string,
): T[] {
  return items.map((item): T => {
    if (item.id !== "Policies" || !item.items) return item
    return {
      ...item,
      items: item.items.map((sub) =>
        sub.url === "/policies/library"
          ? {
              ...sub,
              badge: (
                <span data-policy-approvals-badge="" className="inline-flex min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold tabular-nums text-white">
                  <span aria-hidden="true">{count}</span>
                  <span className="sr-only">{label}</span>
                </span>
              ),
            }
          : sub,
      ),
    }
  })
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // The theme decides the sidebar's style: the builder's under Custom (and for someone who never chose
  // a theme — as it always was), docked on Classic, a ready-made theme's own otherwise (Harbor floats).
  // A card the Theme menu is previewing previews its sidebar too.
  const sidebarVariant = useAppSidebarVariant()
  const features = useFeatureVisibility()
  const enabledModules = useEnabledModules()
  const authUser = useAuthStore((s) => s.user)
  // The shell's `t`: the interface language, and English (never a key path)
  // outside the provider or under a stub that echoes the key.
  const { t } = useShellTranslation()

  // authUser drives the State Machines visibility gate — see
  // STATE_MACHINES_VISIBLE_EMAILS above.
  const companyTier = isCompanyTierRole(authUser?.role)
  const navMain = React.useMemo(
    () => buildNavItems(features, t, authUser?.email ?? null, { companyTier }),
    [features, t, authUser?.email, companyTier],
  )

  // Role gating (owner, 2026-10-05): an entry whose page the role cannot open
  // is hidden; the permission each page needs is in lib/auth/nav-permissions.ts.
  // Fail-open like the module gate: while the grants load (and after a failed
  // fetch) the tree is shown whole, so nothing flashes away and back.
  const access = usePermissions()
  const grants = access.status === "ready" ? access.data?.permissions ?? null : null

  // Policies' "waiting for your approval" count (admin-screens, sidebar): only a holder of
  // create:PolicyApproval is asked (the API answers the drafts that name THIS person and that they
  // did not submit), and only while the module is on. Hidden at zero.
  const policiesOn = enabledModules === null || enabledModules.has("policies")
  const approvalsCount = usePolicyApprovalsCount(policiesOn && access.can("create", "PolicyApproval") === true)
  const approvalsWaiting = approvalsCount.data?.waitingForMe ?? 0

  // Module entitlement gating — fail-open: when enabledModules is null
  // (loading or error) every item is shown. Only hide when we have a
  // definitive enabled set that excludes the item's moduleKey.
  const visibleNav = React.useMemo(() => {
    const allowed = (moduleKey?: string) => !moduleKey || enabledModules === null || enabledModules.has(moduleKey)
    const byModule = navMain
      .filter((item) => allowed(item.moduleKey))
      .map((item) =>
        item.items?.some((sub) => sub.moduleKey)
          ? { ...item, items: item.items.filter((sub) => allowed(sub.moduleKey)) }
          : item,
      )
    const allowedNav = grants === null ? byModule : filterNavByPermission(byModule, grants)
    return approvalsWaiting > 0 ? withPolicyApprovalBadge(allowedNav, approvalsWaiting, t("policies.nav.approvalsBadge", { count: approvalsWaiting })) : allowedNav
  }, [navMain, enabledModules, grants, approvalsWaiting, t])

  // Shown until the signed-in user hydrates (and for a user with neither a
  // name nor an email), so it speaks the interface language too.
  const fallbackName = t("shell.navUser.fallbackName")
  const navUser = {
    name: authUser
      ? [authUser.firstName, authUser.lastName].filter(Boolean).join(' ') || authUser.email || fallbackName
      : fallbackName,
    email: authUser?.email ?? "admin@humanfirewall.ai",
    avatar: resolveBackendFileUrl(authUser?.avatarUrl ?? null) ?? "",
  }

  return (
    <>
      {/* The app's ready-made themes switch on for the page that renders this sidebar, and only
          there: the play area, the portal and the admin console never render it
          (components/app-theme/app-theme-scope.tsx). */}
      <AppThemeScope />
      {/* "start" follows <html dir>: the left in English, the right in Arabic. */}
      <Sidebar side="start" collapsible="icon" variant={sidebarVariant} {...props}>
        <SidebarHeader>
          {/* Product mark above the per-tenant TeamSwitcher so the customer
              always sees the product identity. Shared with the super-admin
              sidebar — see components/brand/sidebar-brand-mark.tsx. */}
          <SidebarBrandMark />
          <TeamSwitcher />
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={visibleNav} />
        </SidebarContent>
        <SidebarFooter>
          <StagingBadge />
          {/* LanguageSwitcher moved to <HeaderControls> top-right cluster
              on 2026-05-10 (closes #185 — operators couldn't find it
              tucked into the sidebar footer; now it sits next to
              ThemeSwitcher in the header). */}
          <NavUser user={navUser} />
          <AppVersion />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
    </>
  )
}
